import { useState } from "react";
import { colors, type Product } from "@/mocks/products";
import type { ProductDetail } from "@/mocks/productDetails";
import { formatBRL } from "@/utils/format";

interface ProductInfoProps {
  product: Product;
  detail: ProductDetail;
  onAddToCart: (product: Product, quantity: number, options: Record<string, string>) => void;
  onBuyNow: (product: Product, quantity: number, options: Record<string, string>) => void;
}

function Stars({ rating }: { rating: number }) {
  const rounded = Math.round(rating);
  return (
    <span className="flex items-center gap-0.5 text-primary-500">
      {[1, 2, 3, 4, 5].map((star) => (
        <i key={star} className={`${star <= rounded ? "ri-star-fill" : "ri-star-line"} text-sm`}></i>
      ))}
    </span>
  );
}

export default function ProductInfo({ product, detail, onAddToCart, onBuyNow }: ProductInfoProps) {
  const [favorite, setFavorite] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selected, setSelected] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    detail.options.forEach((group) => {
      initial[group.name] = group.values[0];
    });
    return initial;
  });

  const colorHex = colors.find((color) => color.id === product.color)?.hex ?? "#c9a24b";
  const pixPrice = product.price * 0.92;

  return (
    <div className="flex w-full flex-col">
      <p className="text-xs uppercase tracking-widest text-foreground-500">
        {detail.sku}
      </p>
      <h1 className="mt-2 font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-4xl">
        {product.name}
      </h1>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Stars rating={detail.rating} />
        <span className="text-sm text-foreground-500">
          {detail.rating.toFixed(1)} · {detail.reviews} avaliações
        </span>
      </div>

      <div className="mt-5 border-t border-background-200 pt-5">
        <div className="flex flex-wrap items-baseline gap-3">
          <span className="font-heading text-3xl font-semibold text-foreground-950">
            {formatBRL(product.price)}
          </span>
          <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-800">
            8% off no Pix
          </span>
        </div>
        <p className="mt-1 text-sm text-foreground-600">
          ou <strong className="font-semibold text-foreground-800">12x de {formatBRL(product.installmentValue)}</strong> sem juros
        </p>
        <p className="mt-0.5 text-sm text-foreground-600">
          {formatBRL(pixPrice)} à vista no Pix
        </p>
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-3">
          <span className="flex h-6 w-6 items-center justify-center">
            <span
              style={{ backgroundColor: colorHex }}
              className="h-5 w-5 rounded-full border border-background-300"
            ></span>
          </span>
          <span className="text-sm text-foreground-700">
            Cor: <strong className="font-medium text-foreground-900">
              {colors.find((color) => color.id === product.color)?.label}
            </strong>
          </span>
        </div>
      </div>

      {detail.options.map((group) => (
        <div key={group.name} className="mt-6">
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-widest text-foreground-800">
            {group.name}
          </p>
          <div className="flex flex-wrap gap-2">
            {group.values.map((value) => {
              const active = selected[group.name] === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSelected((prev) => ({ ...prev, [group.name]: value }))}
                  className={`whitespace-nowrap rounded-md border px-4 py-2 text-sm transition-colors ${
                    active
                      ? "border-primary-500 bg-primary-50 font-medium text-primary-800"
                      : "border-background-300 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  {value}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center justify-between rounded-md border border-background-300 sm:justify-start">
          <button
            type="button"
            aria-label="Diminuir quantidade"
            onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
            className="flex h-11 w-11 items-center justify-center text-foreground-700 transition-colors hover:text-primary-700"
          >
            <i className="ri-subtract-line text-base"></i>
          </button>
          <span className="w-10 text-center text-sm font-medium text-foreground-900">{quantity}</span>
          <button
            type="button"
            aria-label="Aumentar quantidade"
            onClick={() => setQuantity((prev) => Math.min(10, prev + 1))}
            className="flex h-11 w-11 items-center justify-center text-foreground-700 transition-colors hover:text-primary-700"
          >
            <i className="ri-add-line text-base"></i>
          </button>
        </div>

        <button
          type="button"
          onClick={() => onAddToCart(product, quantity, selected)}
          className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-background-50 transition-colors hover:bg-primary-600"
        >
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-shopping-bag-line text-base"></i>
          </span>
          Adicionar ao carrinho
        </button>

        <button
          type="button"
          aria-label={favorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
          onClick={() => setFavorite((prev) => !prev)}
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md border transition-colors ${
            favorite
              ? "border-accent-500 text-accent-600"
              : "border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
          }`}
        >
          <i className={`${favorite ? "ri-heart-fill" : "ri-heart-line"} text-lg`}></i>
        </button>
      </div>

      <button
        type="button"
        onClick={() => onBuyNow(product, quantity, selected)}
        className="mt-3 w-full whitespace-nowrap rounded-md border border-primary-500 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-primary-700 transition-colors hover:bg-primary-50"
      >
        Comprar agora
      </button>

      <div className="mt-7 space-y-3 rounded-lg bg-background-100 p-4">
        <div className="flex items-start gap-3">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
            <i className="ri-truck-line text-base"></i>
          </span>
          <p className="text-sm text-foreground-700">
            <strong className="font-medium text-foreground-900">Frete grátis</strong> para todo o Brasil
          </p>
        </div>
        <div className="flex items-start gap-3">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
            <i className="ri-exchange-box-line text-base"></i>
          </span>
          <p className="text-sm text-foreground-700">
            Troca fácil em até <strong className="font-medium text-foreground-900">30 dias</strong>
          </p>
        </div>
        <div className="flex items-start gap-3">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
            <i className="ri-shield-check-line text-base"></i>
          </span>
          <p className="text-sm text-foreground-700">
            <strong className="font-medium text-foreground-900">Compra 100% segura</strong> e dados protegidos
          </p>
        </div>
      </div>
    </div>
  );
}